import { useEffect, useState, useSyncExternalStore } from 'react';
import { useNavigate } from 'react-router';
import { ApiError, pushApi, type PushSettingsView, type QuietHoursView } from '../api/client';
import { useAuth } from '../lib/auth';
import { plural, type Tone } from '../lib/format';
import { Icon } from '../lib/icons';
import { useData } from '../lib/useData';
import { applyUpdate, dismissInstall, installDismissed, isIos, promptInstall, usePwa } from '../pwa/pwa';
import { onHere, permission, pushSupport, syncHere, turnOffHere, turnOnHere } from '../pwa/push';
import { Button, Notice } from './ui';
import { forgetAlertPrefs, previewMessageSound, setSoundOn, soundOn } from '../lib/alerts';

/**
 * The installable Stitch app in the portal: Your settings → Notifications (this device on or off,
 * previews, quiet hours, a test), installing it (Chrome and Edge ask; iPhone and iPad need Add to
 * Home Screen), opening a tapped notification, and the "new version" prompt.
 */

const errorText = (e: unknown) => (e instanceof ApiError || e instanceof Error ? e.message : 'That didn’t work. Try again.');

/** The share button in Safari: a square with an arrow out of the top. */
function ShareGlyph() {
  return (
    <svg className="share-glyph" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M8 8H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-2M12 3v12M8 7l4-4 4 4" />
    </svg>
  );
}

const ipad = () => /iPad/.test(navigator.userAgent) || (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

/** Adding Stitch to an iPhone's or iPad's Home Screen: the only way notifications work there (iOS 16.4 and later). */
export function IosInstallSteps() {
  return (
    <ol className="install-steps">
      <li>
        Tap <ShareGlyph /> <strong>Share</strong> in Safari’s toolbar ({ipad() ? 'at the top' : 'at the bottom of the screen'}).
      </li>
      <li>
        Choose <strong>Add to Home Screen</strong>, then <strong>Add</strong>.
      </li>
      <li>
        Open <strong>Stitch</strong> from your Home Screen, sign in, and turn on notifications in Your settings.
      </li>
    </ol>
  );
}

/** Install the Stitch app, where the browser can: Chrome and Edge ask; iPhone and iPad have steps. */
export function InstallApp() {
  const pwa = usePwa();
  const [note, setNote] = useState('');
  if (pwa.installed) return null;
  if (pwa.canInstall) {
    return (
      <div className="install-row">
        <img src="/icons/icon-192.png" alt="" width="36" height="36" />
        <span className="grow">
          <strong>Install the Stitch app</strong>
          <small className="muted">It opens from your home screen or dock, like any app.{note ? ` ${note}` : ''}</small>
        </span>
        <Button
          size="small"
          icon="download"
          onClick={() => {
            void promptInstall().then((r) => setNote(r === 'dismissed' ? 'You can install it any time from here.' : ''));
          }}
        >
          Install
        </Button>
      </div>
    );
  }
  if (isIos()) {
    return (
      <div className="install-ios">
        <strong>Install the Stitch app</strong>
        <IosInstallSteps />
      </div>
    );
  }
  return null;
}

/** What this device can do about notifications, in plain words, when it can't simply turn them on. */
function supportText(): string | null {
  switch (pushSupport()) {
    case 'dev':
      return 'Notifications work in the built app, not the development server.';
    case 'insecure':
      return 'Notifications need Stitch to be opened over https.';
    case 'ios-install':
      return 'On iPhone and iPad, notifications come through the Stitch app: add it to your Home Screen first (above), then turn them on there.';
    case 'ios-old':
      return 'Notifications need iOS 16.4 or later on this iPhone or iPad. Update it in Settings → General → Software Update, then come back here.';
    case 'unsupported':
      return 'This browser can’t show notifications from Stitch. Chrome, Edge, Firefox and Safari can.';
    default:
      return null;
  }
}

const BLOCKED = 'Notifications are blocked for Stitch in this browser. Allow them in the site’s settings (the icon beside the address), or for the Stitch app in your phone’s settings, then come back here.';

/** The device's own time zone, for quiet hours. */
const deviceZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';

/** Your settings → Notifications. Changes here save straight away. */
export function NotificationSettings() {
  const { state } = useAuth();
  const userId = state?.user?.id ?? '';
  const [support] = useState(pushSupport);
  const [perm, setPerm] = useState(permission);
  const [here, setHere] = useState<boolean | null>(null);
  const loaded = useData(() => pushApi.settings(), []);
  const [prefs, setPrefs] = useState<PushSettingsView | null>(null);
  const [busy, setBusy] = useState('');
  const [result, setResult] = useState<{ tone: Tone; text: string } | null>(null);

  useEffect(() => {
    if (loaded.data) setPrefs(loaded.data);
  }, [loaded.data]);
  useEffect(() => {
    let alive = true;
    void onHere(userId).then((on) => alive && setHere(on));
    return () => {
      alive = false;
    };
  }, [userId]);

  async function run(what: string, fn: () => Promise<void>) {
    setBusy(what);
    setResult(null);
    try {
      await fn();
    } catch (e) {
      setResult({ tone: 'crit', text: errorText(e) });
    } finally {
      setBusy('');
    }
  }
  const turnOn = () =>
    run('on', async () => {
      const r = await turnOnHere(userId);
      setPerm(permission());
      if (r === 'on') {
        setHere(true);
        setPrefs(await pushApi.settings());
        setResult({ tone: 'ok', text: 'Notifications are on for this device.' });
      } else if (r === 'denied') setResult({ tone: 'warn', text: BLOCKED });
    });
  const turnOff = () =>
    run('off', async () => {
      await turnOffHere(userId);
      setHere(false);
      setPrefs(await pushApi.settings());
    });
  const test = () =>
    run('test', async () => {
      const r = await pushApi.test();
      setResult(
        r.sent > 0
          ? { tone: 'ok', text: `Sent to ${plural(r.sent, 'device')}. It should show in a few seconds.` }
          : { tone: 'warn', text: 'It couldn’t be sent just now. Try again in a minute.' }
      );
      setPrefs(await pushApi.settings());
    });
  async function save(set: { enabled?: boolean; previews?: boolean; quiet?: QuietHoursView | null }) {
    if (!prefs) return;
    const before = prefs;
    setPrefs({ ...prefs, ...set });
    try {
      setPrefs(await pushApi.saveSettings(set));
      // The notifications the open page shows follow the new settings too.
      forgetAlertPrefs();
    } catch (e) {
      setPrefs(before);
      setResult({ tone: 'crit', text: errorText(e) });
    }
  }
  const removeDevice = (id: string) =>
    run(`rm-${id}`, async () => {
      await pushApi.unsubscribe({ id });
      setPrefs(await pushApi.settings());
    });

  const cant = supportText();
  const quiet = prefs?.quiet ?? null;
  return (
    <fieldset className="plain-fieldset notif-settings" aria-describedby="notif-hint">
      <legend className="field-label">Notifications</legend>
      <span className="hint" id="notif-hint">
        New messages and mentions on your phone or computer, even when Stitch isn’t open. Changes here save straight away.
      </span>
      <InstallApp />
      <div className="notif-device" role="status">
        {cant ? (
          <span className="muted">{cant}</span>
        ) : perm === 'denied' ? (
          <span className="muted">{BLOCKED}</span>
        ) : here === null ? (
          <span className="muted">Checking this device…</span>
        ) : (
          <>
            <span className="grow">
              <strong>{here ? 'On for this device' : 'Off for this device'}</strong>
            </span>
            {here ? (
              <Button size="small" disabled={Boolean(busy)} onClick={() => void turnOff()}>
                {busy === 'off' ? 'Turning off…' : 'Turn off'}
              </Button>
            ) : (
              <Button size="small" variant="primary" icon="bell" disabled={Boolean(busy)} onClick={() => void turnOn()}>
                {busy === 'on' ? 'Turning on…' : 'Turn on for this device'}
              </Button>
            )}
          </>
        )}
      </div>
      <MessageSoundChoice />
      {prefs && (
        <>
          <label className="check">
            <input type="checkbox" checked={prefs.previews} onChange={(e) => void save({ previews: e.target.checked })} />
            <span className="cell-stack">
              <span>Show what messages say</span>
              <small className="muted">Off: notifications just say “New message”, without who it’s from or what it says.</small>
            </span>
          </label>
          <label className="check">
            <input type="checkbox" checked={Boolean(quiet)} onChange={(e) => void save({ quiet: e.target.checked ? { from: '22:00', to: '07:00', zone: deviceZone() } : null })} />
            <span className="cell-stack">
              <span>Quiet hours</span>
              <small className="muted">No notifications at these times{quiet ? ` (${quiet.zone.replace(/_/g, ' ')} time)` : ''}.</small>
            </span>
          </label>
          {quiet && (
            <div className="quiet-times">
              <label>
                <span className="sr-only">Quiet from</span>
                <input className="input" type="time" value={quiet.from} onChange={(e) => e.target.value && void save({ quiet: { ...quiet, from: e.target.value } })} />
              </label>
              <span>to</span>
              <label>
                <span className="sr-only">Quiet until</span>
                <input className="input" type="time" value={quiet.to} onChange={(e) => e.target.value && void save({ quiet: { ...quiet, to: e.target.value } })} />
              </label>
            </div>
          )}
          <label className="check">
            <input type="checkbox" checked={!prefs.enabled} onChange={(e) => void save({ enabled: !e.target.checked })} />
            <span className="cell-stack">
              <span>Pause notifications on all your devices</span>
              <small className="muted">Until you untick this. Your devices stay set up.</small>
            </span>
          </label>
          {prefs.devices.length > 0 && (
            <div className="notif-devices">
              <span className="field-label">Getting your notifications</span>
              <ul className="plain-list">
                {prefs.devices.map((d) => (
                  <li key={d.id}>
                    <Icon name="device" size={16} />
                    <span className="grow">
                      {d.label}
                      {d.current ? <span className="muted"> · this one</span> : null}
                    </span>
                    {!d.current && (
                      <button type="button" className="link-btn" disabled={Boolean(busy)} onClick={() => void removeDevice(d.id)}>
                        Remove
                      </button>
                    )}
                  </li>
                ))}
              </ul>
              <div>
                <Button size="small" icon="send" disabled={Boolean(busy)} onClick={() => void test()}>
                  {busy === 'test' ? 'Sending…' : 'Send a test notification'}
                </Button>
              </div>
            </div>
          )}
        </>
      )}
      {loaded.error && !prefs && <Notice tone="crit">{loaded.error.message}</Notice>}
      {result && <Notice tone={result.tone}>{result.text}</Notice>}
      {support === 'ok' && here === false && perm !== 'denied' && (prefs?.devices.length ?? 0) === 0 && (
        <span className="hint">Your browser asks first. Choose Allow.</span>
      )}
    </fieldset>
  );
}

const OFFER_KEY = 'stitch.notify.offer.dismissed';
function offerDismissed(): boolean {
  try {
    return localStorage.getItem(OFFER_KEY) === '1';
  } catch {
    return false;
  }
}

/**
 * In Messages on a computer (browser or the installed app): an offer to turn notifications on for
 * this computer, until they do, decide, or say not now. It asks the browser, then signs this
 * computer up (as Your settings → Notifications does).
 */
export function DesktopNotifyOffer() {
  const { state } = useAuth();
  const userId = state?.user?.id ?? '';
  const [shown, setShown] = useState(() => !offerDismissed() && pushSupport() === 'ok' && permission() === 'default');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState('');
  if (!shown || !userId) return null;
  const close = () => {
    try {
      localStorage.setItem(OFFER_KEY, '1');
    } catch {
      /* private mode */
    }
    setShown(false);
  };
  return (
    <aside className="notify-offer" aria-label="Notifications on this computer">
      <Icon name="bell" size={18} />
      <span className="grow cell-stack">
        <strong>Get notified about new messages</strong>
        <small className="muted">{note || 'On this computer, even when Stitch isn’t in front.'}</small>
      </span>
      <Button
        size="small"
        variant="primary"
        disabled={busy}
        onClick={() => {
          setBusy(true);
          void turnOnHere(userId)
            .then((r) => {
              if (r === 'on') setShown(false);
              else if (r === 'denied') setNote('Blocked in this browser. Allow notifications for Stitch in the site’s settings.');
            })
            .catch((e) => setNote(errorText(e)))
            .finally(() => setBusy(false));
        }}
      >
        {busy ? 'Turning on…' : 'Turn on'}
      </Button>
      <button type="button" className="icon-btn" aria-label="Not now" onClick={close}>
        <Icon name="x" size={15} strokeWidth={2.2} />
      </button>
    </aside>
  );
}

/** The message sound, on this device (lib/alerts): on unless turned off here, with a Play to hear it. */
function MessageSoundChoice() {
  const [on, setOn] = useState(soundOn);
  return (
    <div className="check sound-choice">
      <label className="check grow">
        <input
          type="checkbox"
          checked={on}
          onChange={(e) => {
            setSoundOn(e.target.checked);
            setOn(e.target.checked);
          }}
        />
        <span className="cell-stack">
          <span>Play a sound for new messages</span>
          <small className="muted">On this device, while Stitch is open. Not for the conversation you’re reading, or on do not disturb.</small>
        </span>
      </label>
      <button type="button" className="link-btn small" onClick={() => void previewMessageSound()}>
        <Icon name="play" size={13} /> Play
      </button>
    </div>
  );
}

const narrowQuery = '(max-width: 860px)';
function useNarrow(): boolean {
  return useSyncExternalStore(
    (l) => {
      const m = window.matchMedia(narrowQuery);
      m.addEventListener?.('change', l);
      return () => m.removeEventListener?.('change', l);
    },
    () => window.matchMedia(narrowQuery).matches,
    () => false
  );
}

/** Safari on a Mac, 17 or later: it can add a site to the Dock (File → Add to Dock). Not an iPad. */
function macSafari(): boolean {
  const ua = navigator.userAgent;
  if (!/Macintosh/.test(ua) || navigator.maxTouchPoints > 1) return false;
  if (!/Safari\//.test(ua) || /Chrome|Chromium|CriOS|Edg|Firefox|FxiOS|OPR/.test(ua)) return false;
  return Number(/Version\/(\d+)/.exec(ua)?.[1] ?? 0) >= 17;
}

/**
 * "Install the Stitch app", once (until dismissed), wherever it can be installed: phones and
 * computers whose browser offers it (Chrome, Edge: an Install button), iPhone and iPad (the
 * steps), and Safari on a Mac (the menu to use). Not where it can't (Firefox on a computer).
 */
export function InstallOffer({ inline = false }: { inline?: boolean }) {
  const pwa = usePwa();
  const narrow = useNarrow();
  const [hidden, setHidden] = useState(installDismissed);
  const ios = isIos();
  const mac = !ios && macSafari();
  if (hidden || pwa.installed || !(pwa.canInstall || ios || mac)) return null;
  const close = () => {
    dismissInstall();
    setHidden(true);
  };
  return (
    <aside className={inline ? 'pwa-card inline' : 'pwa-card'} aria-label="Install the Stitch app">
      <img src="/icons/icon-192.png" alt="" width="40" height="40" />
      <div className="grow">
        <strong>Install the Stitch app</strong>
        {ios ? (
          <span>
            For notifications on this {ipad() ? 'iPad' : 'iPhone'}: tap <ShareGlyph /> Share, then <strong>Add to Home Screen</strong>.
          </span>
        ) : mac ? (
          <span>
            Open Stitch from your Dock, in its own window: in Safari’s menu bar, choose <strong>File</strong>, then <strong>Add to Dock</strong>.
          </span>
        ) : narrow ? (
          <span>Open Stitch from your home screen, and get notifications.</span>
        ) : (
          <span>Open Stitch in its own window from your dock or taskbar, and get notifications.</span>
        )}
      </div>
      {!ios && !mac && (
        <Button
          variant="primary"
          size="small"
          onClick={() => {
            void promptInstall().then((r) => r !== 'accepted' && close());
          }}
        >
          Install
        </Button>
      )}
      <button type="button" className="icon-btn" aria-label="Not now" onClick={close}>
        <Icon name="x" size={15} strokeWidth={2.2} />
      </button>
    </aside>
  );
}

/**
 * For signed-in pages (rendered with the menu): a tapped notification opens its page here, a device
 * with notifications on signs up again after signing in, and the install banner.
 */
export function PwaPrompts({ banner = true }: { banner?: boolean }) {
  const navigate = useNavigate();
  const { state } = useAuth();
  // Stitch Admin's own container: notifications (and the app to install) are the portal's.
  const here = state?.surface !== 'admin';
  const userId = here ? state?.user?.id : undefined;
  useEffect(() => {
    const go = (e: Event) => {
      const url = (e as CustomEvent<{ url?: string }>).detail?.url;
      if (!url) return;
      e.preventDefault();
      navigate(url);
    };
    window.addEventListener('stitch:open', go);
    return () => window.removeEventListener('stitch:open', go);
  }, [navigate]);
  useEffect(() => {
    if (userId) void syncHere(userId).catch(() => undefined);
  }, [userId]);
  // The phone app offers it in its Home list instead (pages/tenant/MessagesPhone).
  return banner && here ? <InstallOffer /> : null;
}

/** "A new version of Stitch is ready", with Reload (mounted by main.tsx, outside the app's routes). */
export function UpdatePrompt() {
  const { updateReady } = usePwa();
  const [later, setLater] = useState(false);
  if (!updateReady || later) return null;
  return (
    <div className="toast pwa-update" role="status" aria-live="polite">
      <span className="grow">A new version of Stitch is ready.</span>
      <button type="button" className="toast-action" onClick={applyUpdate}>
        Reload
      </button>
      <button type="button" className="icon-btn" aria-label="Later" onClick={() => setLater(true)}>
        <Icon name="x" size={15} strokeWidth={2.2} />
      </button>
    </div>
  );
}
